import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { categories, MAIN_SITE_URL } from '@/data/categories';
import { IMAGES } from '@/data/images';

export default function Hero() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const filtered = categories.filter((c) => t(c.nameKey).toLowerCase().includes(query.toLowerCase()));

  return (
    <section className="relative min-h-[640px] flex items-center pt-24 pb-16 overflow-hidden">
      <div className="absolute inset-0">
        <img src={IMAGES.hero} alt="USFans Spreadsheet streetwear flat lay" className="w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-b from-background-50/80 via-background-50/55 to-background-50/90" />
      </div>

      <div className="relative z-10 w-full mx-auto max-w-7xl px-4 md:px-6 text-center">
        <span className="inline-flex items-center gap-2 rounded-full bg-primary-100/80 text-primary-800 text-xs font-semibold px-4 py-1.5">
          <i className="ri-sparkling-2-line" />
          {t('home.hero.badge')}
        </span>

        <h1 className="mt-6 font-heading font-bold text-3xl md:text-5xl lg:text-6xl text-foreground-950 leading-tight max-w-4xl mx-auto">
          {t('home.hero.h1')}
        </h1>

        <p className="mt-5 text-base md:text-lg text-foreground-700 leading-relaxed max-w-2xl mx-auto">
          {t('home.hero.subtitle')}
        </p>

        <div className="mt-8 max-w-xl mx-auto relative">
          <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 text-lg" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('home.hero.searchPlaceholder')}
            className="w-full pl-12 pr-4 py-3.5 rounded-full bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300"
          />
        </div>

        <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
          <a
            href={MAIN_SITE_URL}
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            {t('home.hero.ctaPrimary')}
            <i className="ri-arrow-right-line" />
          </a>

          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('home.hero.dropdownLabel')}
              <i className={`ri-arrow-down-s-line text-base transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>

            {open && (
              <div className="absolute left-1/2 -translate-x-1/2 mt-2 w-64 max-h-96 overflow-y-auto rounded-lg bg-background-50 border border-background-200 text-left">
                <div className="px-4 py-2.5 border-b border-background-200 text-xs text-foreground-500">
                  {t('home.hero.dropdownHint')}
                </div>
                {filtered.length === 0 ? (
                  <div className="px-4 py-6 text-sm text-foreground-500 text-center">—</div>
                ) : (
                  filtered.map((c) => (
                    <a
                      key={c.id}
                      href={c.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className={`${c.icon} text-primary-500 w-4 h-4 flex items-center justify-center`} />
                      <span>{t(c.nameKey)}</span>
                    </a>
                  ))
                )}
              </div>
            )}
          </div>

          <a
            href={MAIN_SITE_URL}
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full border-2 border-foreground-900 text-foreground-900 text-sm font-semibold hover:bg-foreground-900 hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer"
          >
            {t('home.hero.ctaSecondary')}
          </a>
        </div>

        <p className="mt-8 text-xs text-foreground-500 flex items-center justify-center gap-2">
          <i className="ri-checkbox-circle-line text-primary-500" />
          {t('home.hero.trustLine')}
        </p>
      </div>
    </section>
  );
}